import React, { useState } from 'react';
import { Pencil, X, ExternalLink } from 'lucide-react';
import './MarkupField.css';

/**
 * MarkupField — reusable display for the "Markup %" input on the
 * product editor + category modal.
 *
 * Two display modes:
 *
 *   1. Inherited (no override set):
 *      Shows the inherited value as a clean badge with a label of
 *      where it comes from ("from store" / "from category"). The
 *      merchant doesn't see an empty box — they see what's already
 *      being applied to this product/category. A pencil button next
 *      to it switches to override mode. A small "Edit store default"
 *      link sits beneath so the merchant can jump to Settings → Pricing.
 *
 *   2. Override (this product/category sets its own value):
 *      Shows the input with the override value, a "Clear override"
 *      pill that drops back to inherited, and a subtle hint reminding
 *      the merchant what was inherited before the override.
 *
 * Props:
 *   - inherited: number | null  — the value that would apply if no override
 *   - inheritedFrom: 'store' | 'category' | null
 *   - value: string  — current override value (empty string = no override)
 *   - onChange: (newValue: string) => void
 *   - onConfigureClick: () => void  — opens Settings → Pricing tab
 *   - label?: string (defaults to 'Markup %')
 */
export default function MarkupField({
  inherited,
  inheritedFrom,
  value,
  onChange,
  onConfigureClick,
  label = 'Markup %',
}) {
  const hasOverride = value !== '' && value != null;
  const [editing, setEditing] = useState(hasOverride);

  const inheritedLabel =
    inheritedFrom === 'store'
      ? 'store default'
      : inheritedFrom === 'category'
        ? 'category'
        : null;

  const startOverride = () => {
    setEditing(true);
    // Pre-fill the input with the inherited value so the merchant sees
    // a sensible starting point, not a blank input.
    if (!hasOverride && inherited != null) onChange(String(inherited));
  };

  const clearOverride = () => {
    setEditing(false);
    onChange('');
  };

  // No store default set anywhere — show empty-state with a strong
  // CTA to set one. The merchant can also type an override directly.
  if (inherited == null && !editing) {
    return (
      <div className="mkf-root">
        <div className="mkf-label">{label}</div>
        <div className="mkf-card mkf-card--empty">
          <div className="mkf-empty-copy">
            <div className="mkf-empty-title">No markup set</div>
            <div className="mkf-empty-sub">
              Set a store-wide default so new stock receives have a sensible suggested sell price out of the box.
            </div>
          </div>
          <div className="mkf-empty-actions">
            <button
              type="button"
              className="mkf-secondary-btn"
              onClick={() => setEditing(true)}
            >
              Set for this product
            </button>
            <button
              type="button"
              className="mkf-primary-btn"
              onClick={onConfigureClick}
            >
              <ExternalLink size={13} />
              Set store default
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Inherited mode — the merchant sees the value that's already
  // applied via inheritance, with a pencil to override.
  if (!editing) {
    return (
      <div className="mkf-root">
        <div className="mkf-label">{label}</div>
        <div className="mkf-card">
          <div className="mkf-row">
            <div className="mkf-value-block">
              <div className="mkf-value">{inherited}%</div>
              <div className="mkf-source">
                Inherited from {inheritedLabel}
              </div>
            </div>
            <button
              type="button"
              className="mkf-secondary-btn"
              onClick={startOverride}
              title="Set a different markup for this item"
            >
              <Pencil size={13} />
              Override
            </button>
          </div>
          {onConfigureClick && inheritedFrom === 'store' && (
            <button
              type="button"
              className="mkf-link-btn"
              onClick={onConfigureClick}
            >
              <ExternalLink size={12} />
              Edit store default
            </button>
          )}
        </div>
      </div>
    );
  }

  // Override mode — input is visible. Clear-override pill restores
  // inheritance.
  return (
    <div className="mkf-root">
      <div className="mkf-label">{label}</div>
      <div className="mkf-card mkf-card--override">
        <div className="mkf-row">
          <div className="mkf-input-wrap">
            <input
              type="number"
              min="0"
              max="999.99"
              step="0.01"
              className="mkf-input"
              value={value}
              onChange={(e) => onChange(e.target.value)}
              placeholder="0"
              autoFocus
            />
            <span className="mkf-input-suffix">%</span>
          </div>
          {inherited != null && (
            <button
              type="button"
              className="mkf-clear-btn"
              onClick={clearOverride}
              title="Drop back to inherited value"
            >
              <X size={12} />
              Use inherited
            </button>
          )}
        </div>
        {inherited != null && (
          <div className="mkf-override-hint">
            Override — was inheriting {inherited}% from {inheritedLabel}
          </div>
        )}
        {inherited == null && (
          <div className="mkf-override-hint">
            No inherited default. Setting this one only applies to this item.
          </div>
        )}
      </div>
    </div>
  );
}
